import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import {
  EmissionSymbol,
  IProcessDetails,
} from '../../../../../../../../../../../../shared/interfaces/IFootprint';
import { ENERGY_SOURCE, EnergySourceUnitMap } from '../../../../PcfWizard.type';
import styles from '../../CalculationDetails.module.css';
import {
  ENERGY_SOURCE_LABEL_KEYS,
  EnergySourceOverviewRow,
  getEnergySourceOverviewRows,
} from '../../helpers';

type EnergyOverviewCardProps = {
  processes?: IProcessDetails[];
};

/** Overview of the energy sources used across all Individual Processes of a calculation. */
const EnergyOverviewCard = ({ processes }: EnergyOverviewCardProps) => {
  const { t } = useTranslation();

  const rows = getEnergySourceOverviewRows(processes);

  if (rows.length === 0) {
    return null;
  }

  const energySourceTemplate = (row: EnergySourceOverviewRow) => {
    return (
      <div className='flex flex-column'>
        <span>{t(ENERGY_SOURCE_LABEL_KEYS[row.source])}</span>

        {row.customName && <span className='text-xs text-color-secondary'>{row.customName}</span>}
      </div>
    );
  };

  const renderEmissionFactor = (
    row: EnergySourceOverviewRow,
    value: number | undefined,
    emissionSymbol: EmissionSymbol,
  ): JSX.Element => {
    return (
      <EmissionValueWithSuffix
        emissionValue={value}
        declaredUnit={EnergySourceUnitMap[row.source]}
        unitaryProductAmount={1}
        emissionSymbol={emissionSymbol}
        abbreviateUnit={true}
        emissionValueDecimals={6}
        numericalValueTextClassName='font-normal'
        suffixTextClassName='font-normal'
      />
    );
  };

  const emissionFactorTemplate = (row: EnergySourceOverviewRow) => {
    return (
      <div className='flex flex-column'>
        {/* Electricity factors are stored in grams, the other sources in kilograms. */}
        {renderEmissionFactor(
          row,
          row.emissionFactor,
          row.source === ENERGY_SOURCE.ELECTRICITY ? EmissionSymbol.G_CO2E : EmissionSymbol.KG_CO2E,
        )}

        {row.convertedEmissionFactor != null && (
          <div className='flex text-xs text-color-secondary'>
            {renderEmissionFactor(row, row.convertedEmissionFactor, EmissionSymbol.KG_CO2E)}
          </div>
        )}
      </div>
    );
  };

  const dataSourceTemplate = (row: EnergySourceOverviewRow) => {
    return row.dataSource;
  };

  return (
    <div
      className={classNames('p-4 border-round-2xl flex w-full flex-column gap-3', styles['card'])}
    >
      <h5 className='p-0 m-0 font-medium'>{t('calculationDetailsPage.sections.energy.title')}</h5>

      <DataTable
        value={rows}
        dataKey='source'
        size='small'
        className='text-sm border-round-xl overflow-hidden'
        tableStyle={{ tableLayout: 'fixed', wordBreak: 'break-word' }}
      >
        <Column
          header={t('calculationDetailsPage.sections.energy.energySource')}
          body={energySourceTemplate}
          style={{ width: '22%' }}
        />
        <Column
          header={t('calculationDetailsPage.sections.energy.emissionFactor')}
          body={emissionFactorTemplate}
          style={{ width: '33%' }}
        />
        <Column
          header={t('calculationDetailsPage.sections.energy.source')}
          body={dataSourceTemplate}
          style={{ width: '45%', wordBreak: 'break-word' }}
        />
      </DataTable>
    </div>
  );
};

export default EnergyOverviewCard;
